<template>
    <hox-modal wrapper-class="signal-modal-wrapper" @close="emit('close')">
        <template #header>
            <div class="signal-form-header">
                {{ isNew ? "Add new signal" : "Edit your signals" }}
                <span>
                    {{
                        isNew
                            ? "Create a signal to define how and when your campaign should be activated."
                            : "Update your signal details."
                    }}
                </span>
            </div>
        </template>

        <div class="signal-form-body">
            <div class="signal-form-content">
                <h3>I want a signal based on</h3>

                <div class="signal-form-list-wrapper">
                    <template v-for="(signal, index) in localSignals" :key="signal.signalValue + index">
                        <SignalModalFormItem
                            :model-value="signal"
                            @update="updateLocalSignal(index, $event)"
                            @delete="deleteSignal(index)"
                        />
                        <div v-if="index !== localSignals.length - 1" class="signal-form-list-divider">AND</div>
                    </template>
                </div>

                <Button
                    data-testid="connect__add-condition-button"
                    class="add-new-condition-button"
                    type="text"
                    @click="addSignal"
                    >+ Add condition</Button
                >
            </div>

            <Button
                class="update-signal-button"
                type="primary"
                :disabled="!canSave"
                data-testid="connect__save-signals-button"
                @click="saveSignals"
            >
                {{ isNew ? "Add signal" : "Update signal" }}
            </Button>
        </div>
    </hox-modal>
</template>

<script lang="ts" setup>
import { ref, type PropType, computed, watch } from "vue";
import equal from "fast-deep-equal";

import SignalModalFormItem from "@/Connect/components/Accounts/PlatformAccountRuleSignalForm.vue";

import type { SignalType, Signal } from "@/Connect/types";

export interface SignalForm {
    signalType?: SignalType | "";
    signalValue: string;
    location?: { displayName: string; locationRef: string };
}

function mapRuleSignalToLocalSignal(signal: Signal) {
    return {
        signalType: signal.signalType,
        signalValue: signal.value,
        location: signal?.locations?.[0]
    } as SignalForm;
}

function mapLocalSignalToRuleSignal(signal: SignalForm, index: number) {
    return {
        order: index + 1,
        signalType: signal.signalType,
        value: signal.signalValue,
        ...(signal.location ? { locations: [signal.location] } : {})
    } as Signal;
}

const { addCondition, ruleSignals } = defineProps({
    addCondition: {
        type: Boolean as PropType<boolean>,
        required: true
    },
    ruleSignals: {
        type: Array as PropType<Signal[]>,
        required: true
    }
});

const emit = defineEmits<{
    (e: "close"): void;
    (e: "saveSignals", data: Partial<Signal>[]): void;
    (
        e: "update",
        data: {
            index: number;
            newData: SignalForm;
        }
    ): void;
}>();

const localSignals = ref(ruleSignals.map(mapRuleSignalToLocalSignal));

const isNew = computed(() => ruleSignals.length === 0);
const signalsToSave = computed(() => localSignals.value.map(mapLocalSignalToRuleSignal));
const canSave = computed(() => {
    if (equal(signalsToSave.value, ruleSignals)) {
        return false;
    }

    return localSignals.value.every(signal => signal.signalValue);
});

function updateLocalSignal(index: number, newData: SignalForm) {
    localSignals.value[index] = newData;
}

function saveSignals() {
    emit("saveSignals", signalsToSave.value);
    emit("close");
}

function addSignal() {
    localSignals.value.push({ signalValue: "" });
}

function deleteSignal(index: number) {
    localSignals.value.splice(index, 1);
}

watch(
    () => addCondition,
    newVal => {
        if (newVal) {
            addSignal();
        }
    },
    {
        immediate: true,
        once: true
    }
);

watch(
    localSignals,
    newVal => {
        if (newVal.length === 0) {
            localSignals.value.push({ signalValue: "" });
        }
    },
    { immediate: true }
);
</script>

<style lang="scss">
.signal-modal-wrapper {
    .hox-modal {
        width: 940px;
    }
}
</style>

<style lang="scss" scoped>
.signal-form-header {
    span {
        display: block;
        font-size: 13px;
        font-weight: normal;
        margin-top: 10px;
    }

    .ivu-date-picker {
        width: 48%;
        margin-right: 4%;
        &:last-child {
            margin-right: 0;
        }
    }
}

.signal-form-body {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.signal-form-content {
    padding: 20px;
    border-radius: 6px;

    background: var(--wpp-grey-color-100);

    display: flex;
    flex-direction: column;
    gap: 6px;

    align-items: flex-start;
}

.signal-form-list-wrapper {
    display: flex;
    flex-direction: column;
    gap: 14px;

    width: 100%;
}

.signal-form-list-divider {
    margin-left: 12px;
    width: 39px;

    border: 1px solid var(--wpp-grey-color-400);
    border-radius: 20px;

    color: #fff;
    background-color: var(--wpp-primary-color-500);

    padding: 5px;

    line-height: 12px;
    font-weight: bold;
    font-size: 12px;

    position: relative;
}

// .signal-form-list-divider::before {
//     position: absolute;
//     // top: -53px;
//     // left: 30px;

//     height: 52px;
//     width: 1px;

//     background: var(--wpp-grey-color-400);
// }

.add-new-condition-button {
    border: none;
}

.update-signal-button {
    align-self: flex-end;
}
</style>
